/**
 * Microsoft Authentication Service
 * 
 * Handles Microsoft Azure AD authentication flow using MSAL
 * Supports redirect-based authentication (recommended for production)
 */

import { PublicClientApplication, AuthenticationResult, AccountInfo } from '@azure/msal-browser';
import { msalConfig, loginRequest } from '../config/msal';

export class MicrosoftAuthService {
  private msalInstance: PublicClientApplication;
  private initialized: boolean = false;

  constructor() {
    this.msalInstance = new PublicClientApplication(msalConfig);
  }

  /**
   * Initialize MSAL instance
   * Must be called before any other MSAL operations
   */
  async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }
    
    try {
      await this.msalInstance.initialize();
      this.initialized = true;
      console.log('MSAL initialized successfully');
    } catch (error) {
      console.error('Error initializing MSAL:', error);
      throw error;
    }
  }

  /**
   * Initiate login with redirect
   * User will be redirected to Microsoft login page
   */
  async loginRedirect(): Promise<void> {
    try {
      await this.msalInstance.loginRedirect(loginRequest);
    } catch (error) {
      console.error('Error during login redirect:', error);
      throw error;
    }
  }

  /**
   * Handle redirect response after authentication
   * Should be called on app initialization to handle redirect from Microsoft
   * 
   * @returns AuthenticationResult if redirect was successful, null otherwise
   */
  async handleRedirect(): Promise<AuthenticationResult | null> {
    try {
      const response = await this.msalInstance.handleRedirectPromise();
      
      if (response) {
        console.log('Microsoft authentication successful');
        console.log('Account:', response.account?.username);
        return response;
      }
      
      return null;
    } catch (error) {
      console.error('Error handling redirect:', error);
      throw error;
    }
  }

  /**
   * Get the current active account
   * 
   * @returns AccountInfo if user is signed in, null otherwise
   */
  getActiveAccount(): AccountInfo | null {
    const accounts = this.msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      return accounts[0];
    }
    return null;
  }

  /**
   * Get ID token for the current user
   * This token is sent to the backend for validation
   * 
   * @returns ID token string or null
   */
  async getIdToken(): Promise<string | null> {
    try {
      const account = this.getActiveAccount();
      if (!account) {
        console.warn('No active account found');
        return null;
      }

      // Try to get token silently first
      const response = await this.msalInstance.acquireTokenSilent({
        ...loginRequest,
        account: account,
      });
      
      return response.idToken;
    } catch (error) {
      console.error('Error acquiring token:', error);
      // If silent token acquisition fails, redirect user to login
      await this.loginRedirect();
      return null;
    }
  }

  /**
   * Logout the current user
   * Clears local session and redirects to Microsoft logout
   */
  async logout(): Promise<void> {
    try {
      const accounts = this.msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        await this.msalInstance.logoutRedirect({
          postLogoutRedirectUri: `${window.location.origin}/login`,
          account: accounts[0],
        });
      }
    } catch (error) {
      console.error('Error during logout:', error);
      throw error;
    }
  }

  /**
   * Check if user is authenticated
   * 
   * @returns true if user has an active account
   */
  isAuthenticated(): boolean {
    return this.getActiveAccount() !== null;
  }
}

// Singleton instance
let msalServiceInstance: MicrosoftAuthService | null = null;

/**
 * Get singleton instance of Microsoft Auth Service
 * Ensures only one MSAL instance is created
 */
export const getMsalService = (): MicrosoftAuthService => {
  if (!msalServiceInstance) {
    console.log('[MicrosoftAuthService] Creating new MSAL service instance');
    msalServiceInstance = new MicrosoftAuthService();
  } else {
    console.log('[MicrosoftAuthService] Reusing existing MSAL service instance');
  }
  return msalServiceInstance;
};

/**
 * Reset the MSAL service instance (useful for testing or error recovery)
 */
export const resetMsalService = (): void => {
  console.log('[MicrosoftAuthService] Resetting MSAL service instance');
  msalServiceInstance = null;
};
